/**
 * support.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";

import { AlertService } from "../common/alert.service";

/**
 * Levels of support commitment / support failures for browser clients.
 */
export enum SupportLevel
{
  Known = 0,
  Likely = 1,
  // Unknown = 2,
  UnsupportedAgent = 3,
  UnsupportedVersion = 4
}

/**
 * Reduced class of human-readable browser user agent labels.
 */
export type UAType =
  "chrome" | "firefox" | "ie" | "edge" | "safari" | "opera" | "other";

/**
 * A set of standard features and categories extracted from the browser
 */
interface Browser
{
  agentString : string;
  type : UAType;
  version : string;
  isMobile : boolean;
}

/**
 * Helper class to embody "semver" semantic versioning labels
 */
export class SemVer
{
  public major : number;
  public minor : number;
  public patch : number;
  constructor (public original : string)
  {
    [, this.major, this.minor, this.patch] =
      original.match(/(\d*)\.(\d*)\.(\d*)/).map(
        (group : string) => Number.parseInt(group, 10));
  }
}

// export type DeviceType = "mobile" | "tablet" | "desktop" | "tv" | "other";

/**
 * Message to present when the user is using an incorrect browser.
 */
const BAD_BROWSER_MESSAGE : string =
`At this time, Advocus only officially supports the Chrome browser. Since \
you are using another browser, you may encounter unforeseen problems.`;

/**
 * Message to present when the user is using a somewhat-old browser version.
 */
const UNTESTED_VERSION_MESSAGE : string =
`Your browser version is over 1 year old. While we do not anticipate any \
functionality issues, please consider upgrading for the best support.`;

/**
 * Message to present when the user's browser is too old to support.
 */
const TOO_OLD_VERSION_MESSAGE : string =
`Your browser version is over 2 years old. We cannot currently guarantee \
support before Chrome 46 and you may hit unforeseen problem.`;

/**
 * The lowest Chrome version to consider "fully supported".
 */
const CHROME_FULL_SUPPORT_MIN : number = 54;
/**
 * The lowest Chrome version to consider "likely supported".
 */
const CHROME_LIKELY_SUPPORT_MIN : number = 46;

/**
 * The {@link SupportService} is responsible for feature detection and handling
 * feature alternatives, as well as complex style manipulations that are hard or
 * impossible to accomplish with CSS alone.
 */
@Injectable()
export class SupportService
{
  constructor (protected alerts : AlertService)
  {
    this.browser = this.categorizeUACheaply(navigator.userAgent);
    this.browserSupportLevel = this.determineBrowserSupport();
  }

  /**
   * What's known about the current user's browser.
   */
  public browser : Browser;

  public browserSupportLevel : SupportLevel;

  /**
   * Present a notification the browser fails any support level conditions.
   * (At time of writing, this will be any non-chrome, non-recent browser.)
   */
  public checkBrowser () : void
  {
    switch (this.browserSupportLevel)
    {
      case SupportLevel.UnsupportedAgent:
        return this.alerts.error(BAD_BROWSER_MESSAGE);
      case SupportLevel.Likely:
        return this.alerts.warn(UNTESTED_VERSION_MESSAGE);
      case SupportLevel.UnsupportedVersion:
        return this.alerts.error(TOO_OLD_VERSION_MESSAGE);
    }
  }

  /**
   * Examine the browser configuration and return a support level determination.
   */
  private determineBrowserSupport () : SupportLevel
  {
    if (this.browser.type !== "chrome")
    {
      // first handle unsupported browser models
      return SupportLevel.UnsupportedAgent;
    }
    if (Number.parseFloat(this.browser.version) < CHROME_LIKELY_SUPPORT_MIN)
    {
      // then ditch really old versions
      return SupportLevel.UnsupportedVersion;
    }
    if (Number.parseFloat(this.browser.version) < CHROME_FULL_SUPPORT_MIN)
    {
      // then flag uncertain versions
      return SupportLevel.Likely;
    }
    // otherwise we're good
    return SupportLevel.Known;
  }

  /**
   * Distill a browser user-agent string into an extremely-naive categorization.
   * In this case, we only care about chrome vs non-chrome and very basic
   * version extraction and mobile determination.
   *
   * NOTE: User-agent strings are notoriously difficult to analyze because their
   * history is rife with theft and copying and nonstandard labels and extra
   * cruft that is anarchical even compared to the baseline of using strings to
   * store complicated key-value lists. Generally we're going to want to use
   * feature detection to actually determine support for specific features
   * because that's a much better and safer and more reliable process. However
   * for an early-stage user intervention, it's useful to examine, especially
   * since we currently have a hard line of "Chrome or bust".
   *
   * @param agentString A user-agent (UA) label
   */
  private categorizeUACheaply (agentString : string) : Browser
  {
    const isMobile : boolean = agentString.indexOf("Mobi") > -1;
    const chrome : RegExpMatchArray =
      agentString.match(/Chrom(?:e|ium)\/(\S*)/);
    if (chrome && chrome.length === 2)
    {
      return {
        agentString,
        type: "chrome",
        isMobile,
        version: chrome[1]
      };
    }
    else
    {
      // TODO: Is it worthwhile to build a "real" UA analyzer? Probably bad
      // practice and may require use of an external library to get right...
      return {
        agentString,
        type: "other",
        isMobile,
        version: "uknown"
      };
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/debug/support.service.ts